import React, { FC, useEffect, useRef, useState } from 'react'
import { Menu } from '@headlessui/react'
import { ArrowDownTrayIcon, EllipsisVerticalIcon, LinkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, DeleteModal, MoveToIcon, Tooltip } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Project } from '../../services/ProjectService'
import AddFolderOrRenameModal from '../modals/AddFolderOrRenameModal'
import DuplicateIcon from '../../assets/DuplicateIcon'
import MoveToModal from '../modals/MoveToModal'
import { useMoveItem } from '../../repositories/project-item/mutations/item-move'
import BpmnIcon from '../../assets/BpmnIcon'
import DmnIcon from '../../assets/DmnIcon'
import FormIcon from '../../assets/FormIcon'
import { formatDate } from './ProjectsTable'
import { useDeleteItem } from '../../repositories/project-item/mutations/item-delete'
import { useRenameItem } from '../../repositories/project-item/mutations/item-rename'
import { useDuplicateItem } from '../../repositories/project-item/mutations/item-duplicate'
import ReplaceIcon from '../../assets/ReplaceIcon'
import FilesAndDirectoriesService from '../../services/FilesAndDirectoriesService'
import { useQueryClient } from 'react-query'
import FolderIcon from '../../assets/FolderIcon'
import { saveAs } from 'file-saver'
import { useBulkDelete } from '../../repositories/project-item/mutations/item-bulk-delete'
import { useItemBulkDuplicate } from '../../repositories/project-item/mutations/item-bulk-duplicate'
import { useItemBulkMove } from '../../repositories/project-item/mutations/item-bulk-move'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline'

export type TableHeaders = {
  accessor: string
  label: string
}
export type TableAction = {
  name: string
  icon?: any
  onClick: () => void
}

type TableProps = {
  headers: TableHeaders[]
  rows: Project[]
  directoryPath?: string
}

export const getFileIcon = (name: string, className?: string) => {
  switch (name.split('.')[1]) {
    case 'bpmn':
      return <BpmnIcon className={className ?? 'w-5 h-5 mr-2'} />
    case 'dmn':
      return <DmnIcon className={className ?? 'w-5 h-5 mr-2'} />
    case 'json':
      return <FormIcon className={className ?? 'w-5 h-5 mr-2'} />
    case 'form':
      return <FormIcon className={className ?? 'w-5 h-5 mr-2'} />
    default:
      return <FolderIcon className={className ?? 'w-5 h-5 mr-2'} />
  }
}

const ProjectContentsTable: FC<TableProps> = ({ headers, rows, directoryPath }) => {
  const [tableData, setTableData] = useState<Project[]>(rows)
  const { org, project } = useParams()
  const [itemToEdit, setItemToEdit] = useState('')
  const [deleteBulkProjectModalOpen, setDeleteBulkProjectModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [moveToModalOpen, setMoveToModalOpen] = useState(false)
  const [moveToBulkModalOpen, setMoveToBulkModalOpen] = useState(false)
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [replaceModalOpen, setReplaceModalOpen] = useState(false)
  const [replaceLoading, setReplaceLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<null | File>(null)
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const { mutate: deleteItem, isLoading: isRemoving } = useDeleteItem(directoryPath)
  const { mutate: deleteBulkItem, isLoading: isBulkRemoving } = useBulkDelete(directoryPath)
  const { mutate: renameItem, isLoading: isRenaming } = useRenameItem(directoryPath)
  const { mutate: duplicateItem } = useDuplicateItem(directoryPath)
  const { mutate: duplicateBulkItem } = useItemBulkDuplicate(directoryPath)
  const { mutate: moveItem } = useMoveItem(directoryPath)
  const { mutate: moveItemBulk, isLoading: isBulkMoving } = useItemBulkMove(directoryPath)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState<string[]>([])

  const [sortField, setSortField] = useState('')
  const [order, setOrder] = useState('asc')

  const handleSorting = (sortField: string, sortOrder: string) => {
    if (sortField) {
      const sorted = [...tableData].sort((a, b) => {
        if (a[sortField] === null) return 1
        if (b[sortField] === null) return -1
        if (a[sortField] === null && b[sortField] === null) return 0
        if (a[sortField].type === 'date' && b[sortField].type === 'date') {
          return (dayjs(a[sortField]).isBefore(b[sortField]) ? -1 : 1) * (sortOrder === 'asc' ? 1 : -1)
        }
        return (
          a[sortField].toString().localeCompare(b[sortField].toString(), 'en', { numeric: true }) *
          (sortOrder === 'asc' ? 1 : -1)
        )
      })
      setTableData(sorted)
    }
  }

  const handleSortingChange = (accessor: string) => {
    if (accessor === 'actions') return
    const sortOrder = accessor === sortField && order === 'asc' ? 'desc' : 'asc'
    setSortField(accessor)
    setOrder(sortOrder)
    handleSorting(accessor, sortOrder)
  }

  const handleDelete = () => {
    if (!org || !itemToEdit || !project) return
    deleteItem(
      {
        organization: org,
        project: project,
        name: itemToEdit,
        type: itemToEdit.includes('.') ? 'files' : 'directories',
      },
      {
        onSuccess: () => {
          setDeleteModalOpen(false)
          setItemToEdit('')
        },
      },
    )
  }

  const handleRename = (newName: string) => {
    if (!org || !itemToEdit || !project) return
    renameItem(
      {
        organization: org,
        path: `${project}/${directoryPath ? `${directoryPath}/` : ''}${itemToEdit}`,
        newName,
        type: itemToEdit.includes('.') ? 'files' : 'directories',
      },
      {
        onSuccess: () => {
          setRenameModalOpen(false)
          setItemToEdit('')
        },
      },
    )
  }

  const handleMove = (newPath: string) => {
    if (!org || !itemToEdit || !project || !newPath) return
    moveItem(
      {
        organization: org,
        path: `${project}/${directoryPath ? `${directoryPath}/` : ''}${itemToEdit}`,
        newPath: `${newPath}`,
        type: itemToEdit.includes('.') ? 'files' : 'directories',
      },
      {
        onSuccess: () => {
          setMoveToModalOpen(false)
          setItemToEdit('')
        },
      },
    )
  }

  const handleBulkMove = (newPath: string) => {
    if (!org || selectedRows.length === 0 || !project || !newPath) return
    moveItemBulk(
      {
        organization: org,
        path: `${project}/${directoryPath ? `${directoryPath}/` : ''}`,
        newPath: `${newPath}/`,
        selectedProjects: selectedRows,
      },
      {
        onSuccess: () => {
          setMoveToBulkModalOpen(false)
        },
      },
    )
  }

  const handleDuplicate = (name) => {
    if (!org || !name) return
    duplicateItem({
      organization: org,
      path: `${project}/${directoryPath ? `${directoryPath}/` : ''}${name}`,
      type: name.includes('.') ? 'files' : 'directories',
    })
  }

  const handleBulkDuplicate = () => {
    if (!org || selectedRows.length === 0) return
    duplicateBulkItem({ organization: org, selectedProjects: selectedRows })
  }

  useEffect(() => {
    setTableData(rows)
  }, [rows])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleButtonClick = (name: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '' // Resetuje vrednost pre otvaranja dijaloga
      fileInputRef.current.accept = `.${name.split('.')[1]}`
    }
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const file = event.target.files[0]
      setSelectedFile(file)
      if (file.name !== itemToEdit) {
        setItemToEdit('')
        //alert('Selected file must have the same name as previous file!')
        toast.error('File must have the same name!')
        return
      }
      setReplaceModalOpen(true)
    }
  }

  const replaceFile = async () => {
    if (!selectedFile) return
    setReplaceLoading(true)
    await FilesAndDirectoriesService.replaceFile(
      org!,
      `${project}/${directoryPath ? `${directoryPath}/` : ''}${selectedFile.name}`,
      selectedFile,
    )
    directoryPath
      ? queryClient.invalidateQueries({ queryKey: ['directory-content', org, project, directoryPath] })
      : queryClient.invalidateQueries({ queryKey: ['project-content', org, project] })
    setReplaceLoading(false)
    setItemToEdit('')
    setSelectedFile(null)
    setReplaceModalOpen(false)
  }

  const handleDownload = (name: string) => {
    FilesAndDirectoriesService.download(
      org!,
      `${project}/${directoryPath ? `${directoryPath}/` : ''}${name}`,
      name.includes('.') ? 'files' : 'directories',
    )
      .then((res) => {
        //   // 1. Dobavi content-type i content-disposition
        const contentType = res.headers['content-type']
        const contentDisposition = res.headers['content-disposition']

        // 2. Probaj da izvučeš ime fajla iz content-disposition
        let fileName = name
        if (contentDisposition && contentDisposition.includes('filename=')) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/)
          if (match && match[1]) {
            fileName = match[1]
          }
        } else {
          // Fallback: ako je zip fajl, dodaj .zip
          if (
            contentType === 'application/zip' ||
            contentType === 'application/octet-stream' ||
            !fileName.includes('.')
          ) {
            fileName += '.zip'
          }
        }

        if (res.data instanceof Blob) {
          saveAs(res.data, fileName)
        } else {
          const blob = new Blob([res.data])
          saveAs(blob, fileName)
        }
      })
      .catch((err) => {
        console.error('Download error:', err)
      })
  }

  const handleBulkDownload = () => {
    selectedRows.forEach((id) => {
      handleDownload(id)
    })
  }

  const handleGetLink = (name: string) => {
    if (name.includes('.')) {
      navigator.clipboard.writeText(
        `${window.location.href}/modeler/${
          name.split('.')[1] === 'form' ? 'form' : name.split('.')[1]
        }/${encodeURIComponent(name)}`,
      )
    } else navigator.clipboard.writeText(`${window.location.href}/${encodeURIComponent(name)}`)

    setItemToEdit(name)
    setCopied(true)
    toast.success(t('copiedMsg'))
    setTimeout(() => {
      setCopied(false)
      setItemToEdit('')
    }, 1000)
  }

  const handleBulkCopyLink = () => {
    const links = selectedRows
      .map(
        (id) =>
          `${
            id.includes('.')
              ? `${window.location.href}/modeler/${id.split('.')[1] === 'form' ? 'form' : id.split('.')[1]}`
              : window.location.href
          }/${encodeURIComponent(id)}`,
      )
      .join('\n')
    navigator.clipboard.writeText(links)
    setCopied(true)
    toast.success(t('copiedMsg'))
    setTimeout(() => {
      setCopied(false)
      // setProjectToEdit('')
    }, 1000)
  }

  const handleBulkDelete = () => {
    if (!org || !project || selectedRows.length === 0) return
    deleteBulkItem(
      { organization: org, project: project, selectedProjects: selectedRows },
      {
        onSuccess: () => {
          setDeleteBulkProjectModalOpen(false)
          setSelectedRows([])
        },
      },
    )
  }

  const handleRowClick = (e: React.MouseEvent, name: string) => {
    if (e.ctrlKey || e.metaKey) {
      // ctrlKey za Windows/Linux, metaKey za MacOS (cmd)
      setSelectedRows((prevSelected) => {
        if (prevSelected.includes(name)) {
          return prevSelected.filter((n) => n !== name)
        } else {
          return [...prevSelected, name]
        }
      })
    } else {
      // navigate(`/${org}/${name}`)
      return
    }
  }

  const clearSelection = () => {
    setSelectedRows([])
  }

  const menuActions = (name: string) => {
    return [
      {
        name: t('duplicate'),
        icon: <DuplicateIcon className='w-4 h-4' />,
        onClick: () => {
          if (!org) return
          handleDuplicate(name)
        },
      },
      {
        name: t('delete'),
        icon: <TrashIcon className='w-4 h-4' />,
        onClick: () => {
          setItemToEdit(name)
          setDeleteModalOpen(true)
        },
      },
      name.includes('.') && {
        name: t('replace'),
        icon: <ReplaceIcon className='w-4 h-4' />,
        onClick: () => {
          setItemToEdit(name)
          handleButtonClick(name)
        },
      },
    ] as TableAction[]
  }

  return (
    <>
      <table className='min-w-full divide-y divide-gray-200 border-collapse'>
        <thead className='text-sm bg-white'>
          <tr className='border-b border-gray-300 w-full'>
            {selectedRows.length > 0 ? (
              <>
                <th colSpan={headers.length + 1} className='flex justify-between items-center w-full'>
                  <div className='px-6 py-3 text-left font-semibold text-gray-500 flex items-center gap-4'>
                    <Button className='!p-0 text-gray-500 hover:text-gray-600' onClick={clearSelection}>
                      ✕
                    </Button>
                    {selectedRows.length} selected
                  </div>
                </th>
                <th></th>
                <th></th>
                <th></th>
                <th>
                  {' '}
                  <div className='flex items-center gap-x-3'>
                    <Tooltip text={t('getLink')}>
                      <Button className='!p-0' onClick={handleBulkCopyLink}>
                        <LinkIcon
                          className={`w-4 h-4 fill-slate-500 hover:fill-gray-800 ${copied ? '!fill-sky-500' : ''}`}
                        />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('delete')}>
                      <Button
                        className='!p-0'
                        onClick={() => {
                          setDeleteBulkProjectModalOpen(true)
                        }}
                      >
                        <TrashIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('download')}>
                      <Button className='!p-0' onClick={handleBulkDownload}>
                        <ArrowDownTrayIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('duplicate')}>
                      <Button className='!p-0' onClick={handleBulkDuplicate}>
                        <DuplicateIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('moveTo')}>
                      <Button
                        className='!p-0'
                        onClick={() => {
                          setMoveToBulkModalOpen(true)
                        }}
                      >
                        <MoveToIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                      </Button>
                    </Tooltip>
                  </div>
                </th>
              </>
            ) : (
              headers?.map(({ label, accessor }, index) => (
                // <th
                //   data-testid='table-header'
                //   key={index}
                //   scope='col'
                //   className='px-6 py-3 text-left font-normal text-slate-600 capitalize tracking-wider cursor-pointer'
                // >
                //   <div className='inline-flex items-center justify-start gap-x-3'>{header.label}</div>
                // </th>
                <th
                  data-testid={`header-${index}`}
                  key={`header-${index}`}
                  className='text-left p-3 font-normal text-slate-500 cursor-pointer'
                  onClick={() => handleSortingChange(accessor)}
                >
                  <div className='inline-flex items-center justify-start gap-x-3 '>
                    {label}

                    {accessor !== 'actions' &&
                      (order === 'asc' ? (
                        <ArrowDownIcon
                          className={classNames(
                            'h-4 w-4',
                            sortField === accessor ? 'text-slate-500' : 'text-transparent',
                          )}
                        />
                      ) : (
                        <ArrowUpIcon
                          className={classNames(
                            'h-4 w-4 text-slate-500',
                            sortField === accessor ? 'text-slate-500' : 'text-transparent',
                          )}
                        />
                      ))}
                  </div>
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody className='bg-white'>
          {tableData?.map((row, index) => (
            <tr
              key={index}
              className={`border-b border-gray-300 px-3 ${
                selectedRows.includes(row.name) ? 'bg-sky-100' : 'hover:bg-blue-50'
              }`}
              data-testid='row'
              onClick={(e) => handleRowClick(e, row.name)}
            >
              {headers?.map((header, index) => {
                const field = header.accessor === 'action' ? row['name'] : row[header.accessor]
                if (header.accessor === 'actions' && selectedRows.length === 0) {
                  return (
                    <td
                      key={index}
                      className='px-6 py-5 whitespace-nowrap text-sm text-gray-500 text-start'
                      data-testid='table-field'
                    >
                      <div className='flex items-center gap-x-3'>
                        <Tooltip text={t('getLink')}>
                          <Button className='!p-0' onClick={() => handleGetLink(row['name'])}>
                            <LinkIcon
                              className={`w-4 h-4 fill-slate-500 hover:fill-gray-800 ${
                                copied && itemToEdit === row['name'] ? '!fill-sky-500' : ''
                              }`}
                            />
                          </Button>
                        </Tooltip>
                        <Tooltip text={t('rename')}>
                          <Button
                            className='!p-0'
                            onClick={() => {
                              setItemToEdit(row['name'])
                              setRenameModalOpen(true)
                            }}
                          >
                            <PencilIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                          </Button>
                        </Tooltip>
                        <Tooltip text={t('moveTo')}>
                          <Button
                            className='!p-0'
                            onClick={() => {
                              setItemToEdit(row['name'])
                              setMoveToModalOpen(true)
                            }}
                          >
                            <MoveToIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                          </Button>
                        </Tooltip>
                        <Tooltip text={t('download')}>
                          <Button
                            className='!p-0'
                            onClick={() => {
                              handleDownload(row['name'])
                            }}
                          >
                            <ArrowDownTrayIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>
                  )
                } else {
                  return (
                    <td
                      key={index}
                      className={`px-6 py-5 whitespace-nowrap text-sm ${
                        header.accessor === 'name' ? 'flex items-center cursor-pointer  ' : ''
                      } text-gray-500 text-start`}
                      data-testid='table-field'
                      onClick={(e) =>
                        header.accessor === 'name' &&
                        !(e.ctrlKey || e.metaKey) &&
                        navigate(
                          `/${org}/${project}/${
                            field.includes('.')
                              ? `${directoryPath ? `${directoryPath}/` : ''}modeler/${
                                  field.split('.')[1] === 'form' ? 'form' : field.split('.')[1]
                                }/${field}`
                              : `${directoryPath ? `${directoryPath}/` : ''}${field}`
                          }`,
                        )
                      }
                    >
                      {header.accessor === 'name' && (
                        <>
                          {getFileIcon(row['name'])}
                          {field}
                        </>
                      )}
                      {header.accessor === 'lastModified' && formatDate(field)}
                      {header.accessor === 'totalSize' && `${(field / 1024).toFixed(2)} KB`}
                    </td>
                  )
                }
              })}
              <td className='p-3'>
                {selectedRows.length === 0 && (
                  <Menu as='div' className='relative inline-block text-left'>
                    <div className=''>
                      <Menu.Button
                        className='inline-flex items-center w-full'
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                      >
                        <EllipsisVerticalIcon className='w-6 h-6 text-black hover:text-slate-400' />
                      </Menu.Button>
                    </div>
                    <Menu.Items className='absolute bg-white z-10 right-0 mt-1 w-56 origin-top-right rounded drop-shadow-lg shadow-lg'>
                      <div className='py-1'>
                        {menuActions(row['name'])?.map((action, index) => (
                          <Menu.Item key={`action-${index}`} as={'div'}>
                            <button
                              className='border-none bg-white flex items-center py-2 px-4 gap-x-2 w-full hover:bg-sky-50'
                              onClick={(e) => {
                                e.stopPropagation()
                                action.onClick()
                              }}
                            >
                              {action.icon}
                              {action.name}
                            </button>
                          </Menu.Item>
                        ))}
                        <input
                          type='file'
                          ref={fileInputRef}
                          className='hidden'
                          onChange={handleFileChange}
                          //accept={`.${itemToEdit.split('.')[1]}`}
                        />
                      </div>
                    </Menu.Items>
                  </Menu>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {deleteModalOpen && (
        <DeleteModal
          title={itemToEdit.includes('.') ? t('deleteFile') : t('deleteFolder')}
          description={itemToEdit.includes('.') ? t('deleteFileMsg') : t('deleteFolderMsg')}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDeleteConfirmed={handleDelete}
          actionLabel={t('delete')!}
          isLoading={isRemoving}
        />
      )}
      {replaceModalOpen && (
        <DeleteModal
          title={t('replace')}
          description={t('uploadToReplaceMsg')}
          open={replaceModalOpen}
          setOpen={setReplaceModalOpen}
          onDeleteConfirmed={replaceFile}
          actionLabel={t('continue')!}
          isLoading={replaceLoading}
        />
      )}
      {deleteBulkProjectModalOpen && (
        <DeleteModal
          title={t('delete')}
          description={t('deleteBulkFilesMsg')}
          open={deleteBulkProjectModalOpen}
          setOpen={setDeleteBulkProjectModalOpen}
          onDeleteConfirmed={handleBulkDelete}
          actionLabel={t('delete')!}
          isLoading={isBulkRemoving}
        />
      )}
      {renameModalOpen && (
        <AddFolderOrRenameModal
          open={renameModalOpen}
          title={itemToEdit.includes('.') ? t('renameFile') : t('renameFolder')}
          setOpen={setRenameModalOpen}
          onCancel={() => setRenameModalOpen(false)}
          initialValue={itemToEdit}
          onCreate={handleRename}
          creating={isRenaming}
          mode='edit'
        />
      )}
      {moveToModalOpen && (
        <MoveToModal
          open={moveToModalOpen}
          setOpen={setMoveToModalOpen}
          onCancel={() => setMoveToModalOpen(false)}
          onMove={handleMove}
        />
      )}
      {moveToBulkModalOpen && (
        <MoveToModal
          open={moveToBulkModalOpen}
          setOpen={setMoveToBulkModalOpen}
          onCancel={() => setMoveToBulkModalOpen(false)}
          onMove={handleBulkMove}
          loading={isBulkMoving}
        />
      )}
    </>
  )
}

export default ProjectContentsTable
